import { Billboard, Html, Text } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import { useFrame } from "@react-three/fiber";
import { useRef, useState } from "react";
import type { Mesh } from "three";
import { demoteFromSpatial, updateBlock } from "../state/graph-actions";
import {
  type EncounterState,
  useBlock,
  useRelatedBlocks,
} from "../state/graph-hooks";
import type { BlockNode, NodePosition } from "../state/graph-types";
import { useInteractionStore } from "../state/interaction-store";
import { useToolStore } from "../state/tool-store";
import { useUIStore } from "../state/ui-store";
import { useYDoc } from "../state/yjs-provider";
import {
  COMMON_CONDITIONS,
  ConditionBadges,
  parseConditions,
  setConditionsValue,
} from "./condition-badges";
import { HpBar } from "./hp-bar";

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function Entity3D({
  id,
  encounter,
}: {
  id: string;
  encounter: EncounterState | null;
}) {
  const doc = useYDoc();
  const node = useBlock<BlockNode & { position?: NodePosition }>(id);
  const meshRef = useRef<Mesh>(null);
  const activeTool = useToolStore((s) => s.activeTool);
  const setRightTab = useUIStore((s) => s.setRightTab);
  const openJournal = useUIStore((s) => s.openJournal);
  const setContextMenu = useUIStore((s) => s.setContextMenu);
  const relatedLinks = useRelatedBlocks(id);

  const hovered = useInteractionStore((s) => s.hovered);
  const selection = useInteractionStore((s) => s.selection);
  const dragState = useInteractionStore((s) => s.dragState);

  const setHovered = useInteractionStore((s) => s.setHovered);
  const toggleSelection = useInteractionStore((s) => s.toggleSelection);
  const selectExclusive = useInteractionStore((s) => s.selectExclusive);
  const startDrag = useInteractionStore((s) => s.startDrag);
  const [selectedCondition, setSelectedCondition] = useState<
    (typeof COMMON_CONDITIONS)[number]
  >(COMMON_CONDITIONS[0]);
  const ringPulse = useRef(0);
  const isTurnParticipant =
    encounter?.turnOrder.some((entry) => entry.blockId === id) &&
    (node?.type === "token" || node?.type === "npc");
  const turnOrderIndex =
    encounter?.turnOrder.findIndex((entry) => entry.blockId === id) ?? -1;
  const isActiveTurn =
    isTurnParticipant &&
    encounter?.status === "active" &&
    turnOrderIndex === encounter.currentTurnIndex;
  const isActiveEncounter = encounter?.status !== "ended";
  const shouldShowTurnOrder =
    isTurnParticipant && turnOrderIndex >= 0 && isActiveEncounter;

  useFrame((_state, delta) => {
    if (!isActiveTurn) {
      ringPulse.current = 0;
      return;
    }
    ringPulse.current += delta * 2.5;
  });

  // Safely unmount if the node was deleted globally.
  if (!node) return null;

  const spatialPosition =
    node.spatialTransform?.position ??
    // Legacy fallback while old nodes are still present.
    node.position;

  // Edgeless model: only spatial blocks are rendered in R3F.
  if (!spatialPosition) return null;

  const isHovered = hovered === id;
  const isSelected = selection.includes(id);
  const isDragging = dragState?.activeNodeIds.includes(id);

  const hpMax = toFiniteNumber(node.properties?.maxHp) ?? 0;
  const hp = toFiniteNumber(node.properties?.hp) ?? hpMax;
  const conditions = parseConditions(node.properties?.conditions);
  const hasCombatType =
    node.type === "token" || node.type === "npc" || node.type === "actor";
  const showCombatOverlay = hpMax > 0 || (conditions && conditions.length > 0);
  const showHud = isSelected || showCombatOverlay;
  const normalizedHp = hpMax > 0 ? Math.max(0, Math.min(hp, hpMax)) : 0;
  const showCombatControls = isSelected && hasCombatType && hpMax > 0;
  const width =
    typeof node.metadata?.width === "number" ? node.metadata.width : 2;
  const depth =
    typeof node.metadata?.depth === "number" ? node.metadata.depth : 2;
  const hudOffsetY = node.type === "text" ? 1.5 : 1.3;
  const overlayWidth = Math.max(120, Math.round(Math.max(width, depth) * 72));

  const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
    if (activeTool !== "select") return;
    e.stopPropagation();
    setHovered(id);
    document.body.style.cursor = "grab";
  };

  const handlePointerOut = () => {
    if (hovered === id) {
      setHovered(null);
      document.body.style.cursor = "auto";
    }
  };

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (activeTool !== "select") return;
    e.stopPropagation();

    if (e.shiftKey) {
      toggleSelection(id);
    } else {
      selectExclusive(id);
    }

    startDrag([id], { x: e.point.x, y: e.point.y, z: e.point.z });
    document.body.style.cursor = "grabbing";
  };

  const handleContextMenu = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    document.body.style.cursor = "auto";
    setHovered(null);
    setContextMenu({
      entityId: id,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const updateGameplayProperty = (changes: Record<string, unknown>) => {
    const props = {
      ...(node.properties ?? {}),
      ...changes,
    };
    updateBlock(doc, id, { properties: props } as any);
  };

  const setHp = (next: number) => {
    if (hpMax <= 0) return;
    const clamped = Math.max(0, Math.min(next, hpMax));
    updateGameplayProperty({ hp: clamped });
  };

  const adjustHp = (delta: number) => {
    if (hpMax <= 0) return;
    setHp(normalizedHp + delta);
  };

  const promptForAmount = (kind: "damage" | "heal") => {
    if (hpMax <= 0) return;
    const response = window.prompt(
      `${kind === "damage" ? "Damage" : "Heal"} amount`,
      "1",
    );
    if (response === null) return;

    const amount = Number(response);
    if (!Number.isFinite(amount) || amount < 0) return;
    if (kind === "damage") {
      setHp(normalizedHp - amount);
    } else {
      setHp(normalizedHp + amount);
    }
  };

  const toggleCondition = (condition: string) => {
    if (!condition) return;
    const nextCondition = setConditionsValue(conditions, condition);
    updateGameplayProperty({ conditions: nextCondition });
  };

  const label =
    node.name ||
    (typeof node.content === "string" ? node.content.slice(0, 32) : null) ||
    node.type;
  const textContent =
    typeof node.content === "string"
      ? node.content
      : typeof node.name === "string"
        ? node.name
        : label;

  let x = spatialPosition.x;
  let y = spatialPosition.y;
  let z = spatialPosition.z;

  if (isDragging && dragState) {
    x += dragState.delta.x;
    y += dragState.delta.y; // Keep 2D delta typically on plane
    z += dragState.delta.z;
  }

  const hoverColor = isHovered ? "#ff60a0" : "#ff9020";
  const finalColor = isSelected ? "#00ff00" : hoverColor;

  return (
    <group position={[x, y, z]}>
      {isTurnParticipant && encounter && isActiveEncounter && (
        <mesh
          position={[0, 0.02, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          scale={
            isActiveTurn
              ? [
                  0.85 + Math.sin(ringPulse.current) * 0.12,
                  1,
                  0.85 + Math.sin(ringPulse.current) * 0.12,
                ]
              : [1, 1, 1]
          }
        >
          <ringGeometry args={[0.28, 0.45, 48]} />
          <meshStandardMaterial
            color={isActiveTurn ? "#22c55e" : "#facc15"}
            transparent
            opacity={isActiveTurn ? 0.9 : 0.55}
          />
        </mesh>
      )}

      {node.type === "text" ? (
        <group>
          <mesh
            ref={meshRef}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
            onPointerDown={handlePointerDown}
            onContextMenu={handleContextMenu}
            position={[0, 0.55, 0]}
          >
            <boxGeometry args={[2.3, 3, 0.1]} />
            <meshStandardMaterial color={isSelected ? "#164e63" : "#f8fafc"} />
          </mesh>
          <mesh position={[0.72, 1.35, 0.055]}>
            <planeGeometry args={[0.4, 0.55]} />
            <meshStandardMaterial color="#e2e8f0" />
          </mesh>
          <Text
            position={[0, 1.52, 0.06]}
            fontSize={0.13}
            color={isSelected ? "#22d3ee" : "#0f172a"}
            anchorX="center"
            anchorY="middle"
            maxWidth={1.7}
          >
            {node.name ?? "Journal"}
          </Text>
        </group>
      ) : node.type === "shape" || node.type === "drawing" ? (
        <mesh
          ref={meshRef}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          onPointerDown={handlePointerDown}
          onContextMenu={handleContextMenu}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, 0.02, 0]}
        >
          <planeGeometry args={[width, depth]} />
          <meshStandardMaterial color={finalColor} transparent opacity={0.45} />
        </mesh>
      ) : node.type === "actor" ? (
        <mesh
          ref={meshRef}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          onPointerDown={handlePointerDown}
          onContextMenu={handleContextMenu}
          position={[0, 0.45, 0]}
        >
          <capsuleGeometry args={[0.28, 0.8, 4, 8]} />
          <meshStandardMaterial color={finalColor} />
        </mesh>
      ) : node.type === "prop" || node.type === "3d-model" ? (
        <mesh
          ref={meshRef}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          onPointerDown={handlePointerDown}
          onContextMenu={handleContextMenu}
          position={[0, 0.45, 0]}
        >
          <boxGeometry args={[0.9, 0.9, 0.9]} />
          <meshStandardMaterial color={finalColor} />
        </mesh>
      ) : (
        <mesh
          ref={meshRef}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          onPointerDown={handlePointerDown}
          onContextMenu={handleContextMenu}
        >
          <cylinderGeometry args={[0.4, 0.4, 0.1, 32]} />
          <meshStandardMaterial color={finalColor} />
        </mesh>
      )}

      {node.type !== "text" && (
        <Text
          position={[0, 0.95, 0]}
          fontSize={0.18}
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {label}
        </Text>
      )}

      {shouldShowTurnOrder && (
        <Text
          position={[0, isActiveTurn ? 1.45 : 1.22, 0]}
          fontSize={isActiveTurn ? 0.16 : 0.14}
          color={isActiveTurn ? "#22c55e" : "rgba(255,255,255,0.75)"}
          anchorX="center"
          anchorY="middle"
        >
          {turnOrderIndex + 1}
        </Text>
      )}

      {showHud && (
        <Billboard
          follow
          lockX={false}
          lockY={false}
          lockZ={false}
          position={[0, hudOffsetY, 0]}
        >
          <Html transform distanceFactor={9} occlude={false}>
            <div
              style={{
                minWidth: node.type === "text" ? 240 : 160,
                maxWidth: 260,
                borderRadius: 8,
                border: isSelected
                  ? "1px solid rgba(34, 211, 238, 0.75)"
                  : "1px solid rgba(255,255,255,0.12)",
                background: "rgba(15, 23, 42, 0.94)",
                color: "white",
                padding: "8px 10px",
                boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
                backdropFilter: "blur(8px)",
                fontSize: 11,
                lineHeight: 1.35,
                pointerEvents: "auto",
              }}
            >
              {hpMax > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <HpBar
                    current={normalizedHp}
                    max={hpMax}
                    width={overlayWidth}
                  />
                </div>
              )}

              {conditions.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <ConditionBadges
                    conditions={conditions}
                    interactive={showCombatControls}
                    onToggle={showCombatControls ? toggleCondition : undefined}
                  />
                </div>
              )}

              {showCombatControls && (
                <div
                  style={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    padding: "6px 7px",
                    marginBottom: 6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        opacity: 0.8,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Quick HP
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          adjustHp(-1);
                        }}
                        style={hudButtonStyle}
                      >
                        -1
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          adjustHp(1);
                        }}
                        style={hudButtonStyle}
                      >
                        +1
                      </button>
                    </div>
                  </div>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span style={{ fontSize: 10, opacity: 0.75 }}>Set HP</span>
                    <input
                      type="number"
                      value={Math.round(normalizedHp)}
                      min={0}
                      max={hpMax}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        const target = event.target.value;
                        if (target === "") return;
                        const next = Number(target);
                        if (Number.isFinite(next)) {
                          setHp(next);
                        }
                      }}
                      style={{
                        width: "56px",
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 4,
                        color: "white",
                        fontSize: 10,
                        padding: "2px 4px",
                      }}
                    />
                  </label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        promptForAmount("damage");
                      }}
                      style={hudButtonStyle}
                    >
                      Damage
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        promptForAmount("heal");
                      }}
                      style={hudButtonStyle}
                    >
                      Heal
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <select
                      value={selectedCondition}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        const nextCondition = event.target
                          .value as (typeof COMMON_CONDITIONS)[number];
                        setSelectedCondition(nextCondition);
                        toggleCondition(nextCondition);
                      }}
                      style={{
                        flex: 1,
                        background: "rgba(255,255,255,0.08)",
                        color: "white",
                        border: "1px solid rgba(255,255,255,0.22)",
                        borderRadius: 4,
                        fontSize: 10,
                        padding: "2px 4px",
                      }}
                    >
                      {COMMON_CONDITIONS.map((condition) => (
                        <option
                          key={condition}
                          value={condition}
                          style={{
                            background: "rgba(15,23,42,1)",
                          }}
                        >
                          {condition}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleCondition(selectedCondition);
                      }}
                      style={hudButtonStyle}
                    >
                      Toggle
                    </button>
                  </div>
                </div>
              )}

              {(node.type === "text" || isSelected) && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 10,
                          opacity: 0.6,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {node.type}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 160,
                        }}
                      >
                        {node.name ?? label}
                      </div>
                    </div>

                    {isSelected && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {node.type === "text" && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              selectExclusive(id);
                              openJournal(id);
                            }}
                            style={hudButtonStyle}
                          >
                            open
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            selectExclusive(id);
                            setRightTab("inspector");
                          }}
                          style={hudButtonStyle}
                        >
                          inspect
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            demoteFromSpatial(doc, id);
                          }}
                          style={hudButtonStyle}
                        >
                          hide
                        </button>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: node.type === "text" ? 6 : 3,
                      WebkitBoxOrient: "vertical",
                      color: "rgba(255,255,255,0.86)",
                      marginBottom: relatedLinks.length > 0 ? 8 : 0,
                    }}
                  >
                    {textContent}
                  </div>

                  {node.type === "text" && relatedLinks.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          opacity: 0.6,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          marginBottom: 4,
                        }}
                      >
                        Related
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        {relatedLinks.slice(0, 4).map((link) => (
                          <RelatedBlockBadge
                            key={link.edgeId}
                            id={link.relatedBlockId}
                            edgeType={link.edgeType}
                            onSelect={() =>
                              selectExclusive(link.relatedBlockId)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Html>
        </Billboard>
      )}
    </group>
  );
}

const hudButtonStyle: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.04)",
  color: "white",
  borderRadius: 6,
  fontSize: 10,
  lineHeight: 1,
  padding: "4px 6px",
  cursor: "pointer",
  textTransform: "uppercase",
};

function RelatedBlockBadge({
  id,
  edgeType,
  onSelect,
}: {
  id: string;
  edgeType: string;
  onSelect: () => void;
}) {
  const block = useBlock<BlockNode>(id);
  if (!block) return null;

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      style={{
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.04)",
        color: "white",
        borderRadius: 6,
        fontSize: 10,
        padding: "4px 6px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {block.name ?? id}
      </span>
      <span style={{ opacity: 0.65, textTransform: "uppercase" }}>
        {edgeType}
      </span>
    </button>
  );
}
